import { z } from "zod";

// ── Exclusion keywords (admin blocklist) ──

// Blocklist term used to deactivate out-of-scope profiles (e.g. "stagiaire",
// "étudiant") during manual scans and post-enrichment checks.
export const ExclusionKeywordSchema = z.object({
    id: z.uuid(),
    word: z.string(),
    created_at: z.string(),
});
export type ExclusionKeyword = z.infer<typeof ExclusionKeywordSchema>;

export const ExclusionKeywordListResponseSchema = z.array(ExclusionKeywordSchema);
export type ExclusionKeywordListResponse = z.infer<typeof ExclusionKeywordListResponseSchema>;

export const CreateExclusionKeywordRequestSchema = z.object({
    word: z.string().trim().min(1).max(64),
});
export type CreateExclusionKeywordRequest = z.infer<typeof CreateExclusionKeywordRequestSchema>;

// ── Scan ──

// Deactivates active, non-bypassed profiles whose job title / about / company
// description match any keyword. Returns how many profiles were deactivated, and
// how many matching profiles were skipped because they are immunised
// (`exclusion_bypassed`: set when an admin restores a scan-deactivated profile).
// Without the second figure, a scan over an immunised population reads as broken:
// "0 deactivated" facing a list where the admin can see matching profiles.
export const ScanExclusionsResponseSchema = z.object({
    deactivated_count: z.number().int(),
    bypassed_count: z.number().int(),
});
export type ScanExclusionsResponse = z.infer<typeof ScanExclusionsResponseSchema>;

// Count shown in the confirm dialog before the scan runs (same match query, no
// writes). `bypassed_count` mirrors the scan response for the same reason.
export const ScanPreviewResponseSchema = z.object({
    match_count: z.number().int(),
    bypassed_count: z.number().int(),
});
export type ScanPreviewResponse = z.infer<typeof ScanPreviewResponseSchema>;

// ── HTTP envelopes ({ success, data }): the shapes the routes declare as their
// Fastify response schema; `success(reply, data)` fills them. ──

export const ExclusionKeywordListDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ExclusionKeywordListResponseSchema,
});
export type ExclusionKeywordListDetailResponse = z.infer<typeof ExclusionKeywordListDetailResponseSchema>;

export const ExclusionKeywordDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ExclusionKeywordSchema,
});
export type ExclusionKeywordDetailResponse = z.infer<typeof ExclusionKeywordDetailResponseSchema>;

export const ScanExclusionsDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ScanExclusionsResponseSchema,
});
export type ScanExclusionsDetailResponse = z.infer<typeof ScanExclusionsDetailResponseSchema>;

export const ScanPreviewDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ScanPreviewResponseSchema,
});
export type ScanPreviewDetailResponse = z.infer<typeof ScanPreviewDetailResponseSchema>;
